import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Attachment, getExtension } from '@jms/model';
import { PAGE_COMPONENTS } from '@workbuddy/ui/blocks/page';
import { AttachmentsComponent, AttachmentsDisplayMode } from '@workbuddy/ui/components/attachments';
import { DIALOG_CONFIG, DialogConfigExtended } from '@workbuddy/ui/services';
import { ButtonComponent } from '@workbuddy/ui/components/button';
import { DownloadService } from '@workbuddy/ui/components/attachment';
import { iconMap } from '@workbuddy/ui/fields/upload/model';

export interface AttachmentPreviewDialogData {
  attachments: Attachment[];
  attachmentId: string;
  title?: string;
}

@Component({
  selector: 'attachment-preview-dialog',
  templateUrl: './attachment.preview.dialog.component.html',
  styleUrls: ['./attachment.preview.dialog.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, PAGE_COMPONENTS, AttachmentsComponent, ButtonComponent]
})
export class AttachmentPreviewDialogComponent {

  private readonly config = inject<DialogConfigExtended>(DIALOG_CONFIG, { optional: true });
  private readonly downloadService = inject(DownloadService);

  protected $attachments = signal<Attachment[]>(this.config?.data?.attachments ?? []);
  protected $attachmentId = signal<string>(this.config?.data?.attachmentId ?? null);
  protected $title = signal<string>(this.config?.data?.title ?? 'Preview');
  protected $attachment = computed<Attachment | null>(() => this.$attachments()?.find(attachment => attachment._id === this.$attachmentId()) ?? null);
  protected $attachmentTitle = computed<string>(() => this.$attachment()?.title || this.$title());
  protected $attachmentIcon = computed<string>(() => {

    const attachment = this.$attachment();
    if (!attachment) return '';

    if (attachment.type === 'Form') return 'fa-solid fa-file-lines';

    const extension = `.${getExtension(attachment.path)}`;
    return iconMap.get(extension?.toLowerCase()) ?? 'fa-regular fa-file';
  });
  protected $showDownload = computed<boolean>(() => {

    const attachment = this.$attachment();
    if (!attachment?.path) return false;

    return !attachment.path.endsWith('pdf') && attachment.type !== 'Form';
  });

  protected AttachmentsDisplayMode = AttachmentsDisplayMode;
  protected downloadIcon = 'fa-solid fa-download';

  protected onAttachmentSelected(attachment: Attachment): void {

    this.$attachmentId.set(attachment?._id);
  }

  protected downloadFile(): void {

    const attachment = this.$attachment();
    if (!attachment) return;

    this.downloadService.download(attachment);
  }

  protected onClose(): void {

    this.config?.close?.();
  }
}
